import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Navigate } from "react-router-dom";
import {
  Button,
  Container,
  Divider,
  Dropdown,
  Form,
  Header,
  Label,
  Message,
  Segment,
  Table,
} from "semantic-ui-react";

import { PATHS } from "../../../paths";
import { useIsAdminLoading } from "../../../hooks/isAdmin";
import {
  GET_AUDIT_TAG_OPTIONS,
  GET_COURSE_DELIVERY_AUDITS,
} from "../../../queries/getCourseDeliveryAudits";
import { SET_COURSE_DELIVERY_AUDIT } from "../../../mutations/SET_COURSE_DELIVERY_AUDIT";

type TagOption = { id: number; name: string; code: string | null };
type AuditPoint = {
  period: string;
  blockTagId: number;
  confidence: number | null;
  capacity: number | null;
  connection: number | null;
  contribution: number | null;
  notes: string | null;
};

const SCORES = [
  { key: "confidence", label: "Confidence", colour: "#fff3cd" },
  { key: "capacity", label: "Capacity", colour: "#d4edda" },
  { key: "connection", label: "Connection", colour: "#d1ecf1" },
  { key: "contribution", label: "Contribution", colour: "#f8d7da" },
] as const;

type ScoreKey = typeof SCORES[number]["key"];
type Draft = Record<ScoreKey, string> & { notes: string };

const EMPTY_DRAFT: Draft = {
  confidence: "",
  capacity: "",
  connection: "",
  contribution: "",
  notes: "",
};

const show = (value: number | null) => (value == null ? "—" : String(value));

/**
 * Where Joyya records how well it delivered its courses in a community.
 *
 * One audit per community per block — courses are audited four times a year — so the form is a
 * community picker, a block picker and four scores. Saving the same block again corrects that
 * audit rather than adding a second one; the table underneath shows what is already recorded,
 * oldest first, which is the series the apps plot.
 *
 * Deliberately not on /partnerships: that page edits one supporter relationship at a time, and an
 * audit belongs to the community itself — two partner churches supporting the same community
 * share one audit rather than keeping their own.
 */
export const CourseDeliveryAudit = () => {
  const [isAdmin, adminLoading] = useIsAdminLoading();

  const { data: tagData } = useQuery(GET_AUDIT_TAG_OPTIONS);
  const communities: TagOption[] = tagData?.communities ?? [];
  const blocks: TagOption[] = tagData?.blocks ?? [];

  const [communityId, setCommunityId] = useState<number | null>(null);
  const [blockId, setBlockId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft>({ ...EMPTY_DRAFT });
  const [savedNote, setSavedNote] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: auditData, refetch } = useQuery(GET_COURSE_DELIVERY_AUDITS, {
    variables: { communityTagId: communityId },
    skip: communityId == null,
  });
  const audits: AuditPoint[] = auditData?.courseDeliveryAudits ?? [];

  const [save, { loading: saving }] = useMutation(SET_COURSE_DELIVERY_AUDIT);

  // One community and one block are almost always what you want; pick them so the common case
  // is "type four numbers and save". The newest block is last, so that's the one to default to.
  useEffect(() => {
    if (communityId == null && communities.length === 1) setCommunityId(communities[0].id);
    if (blockId == null && blocks.length) setBlockId(blocks[blocks.length - 1].id);
  }, [communities, blocks, communityId, blockId]);

  // Re-seed the four inputs whenever the selection moves, so the form always shows what is
  // currently recorded for that pair rather than the last thing typed against another block.
  const existing = useMemo(
    () => audits.find((a) => a.blockTagId === blockId) ?? null,
    [audits, blockId],
  );
  useEffect(() => {
    setDraft(
      existing == null
        ? { ...EMPTY_DRAFT }
        : {
            confidence: existing.confidence == null ? "" : String(existing.confidence),
            capacity: existing.capacity == null ? "" : String(existing.capacity),
            connection: existing.connection == null ? "" : String(existing.connection),
            contribution: existing.contribution == null ? "" : String(existing.contribution),
            notes: existing.notes ?? "",
          },
    );
  }, [existing, communityId, blockId]);

  if (!adminLoading && !isAdmin) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const outOfRange = SCORES.some(({ key }) => {
    const raw = draft[key];
    if (raw === "") return false;
    const value = Number(raw);
    return Number.isNaN(value) || value < 0 || value > 100;
  });

  const handleSave = async () => {
    if (communityId == null || blockId == null) return;
    setSaveError(null);
    try {
      await save({
        variables: {
          communityTagId: communityId,
          blockTagId: blockId,
          // "" means unscored, which must stay distinct from a scored zero.
          confidence: draft.confidence === "" ? null : Number(draft.confidence),
          capacity: draft.capacity === "" ? null : Number(draft.capacity),
          connection: draft.connection === "" ? null : Number(draft.connection),
          contribution: draft.contribution === "" ? null : Number(draft.contribution),
          notes: draft.notes.trim() ? draft.notes.trim() : null,
        },
      });
      await refetch();
      setSavedNote(true);
      setTimeout(() => setSavedNote(false), 2500);
    } catch (e: any) {
      setSaveError(e?.message ?? "Failed to save");
    }
  };

  const blockLabel = (id: number | null) =>
    blocks.find((b) => b.id === id)?.code ?? blocks.find((b) => b.id === id)?.name ?? "";

  return (
    <Container style={{ marginTop: "2em", marginBottom: "4em" }}>
      <Header as="h1">Course delivery audit</Header>
      <Message info>
        <Message.Header>What this measures</Message.Header>
        <p>
          How well the Collective delivered its teaching in a community — not how the students
          scored. Courses are audited once a block, four times a year, and each of the four Cs is
          scored out of 100. These are the numbers the apps chart on the community page.
        </p>
      </Message>

      <Segment>
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Community</label>
              <Dropdown
                selection
                placeholder="Select a community…"
                value={communityId ?? undefined}
                options={communities.map((c) => ({
                  key: c.id,
                  value: c.id,
                  text: c.code ? `${c.name} (${c.code})` : c.name,
                }))}
                onChange={(_, { value }) => setCommunityId(Number(value))}
              />
            </Form.Field>
            <Form.Field>
              <label>Block</label>
              <Dropdown
                selection
                placeholder="Select a block…"
                value={blockId ?? undefined}
                options={blocks.map((b) => ({
                  key: b.id,
                  value: b.id,
                  text: b.code ? `${b.code} — ${b.name}` : b.name,
                }))}
                onChange={(_, { value }) => setBlockId(Number(value))}
              />
            </Form.Field>
          </Form.Group>
        </Form>

        {communityId != null && blockId != null && (
          <>
            <Divider />
            <Header as="h3">
              {blockLabel(blockId)}
              <Label color={existing ? "blue" : "grey"} size="small" style={{ marginLeft: "0.75em" }}>
                {existing ? "Editing the recorded audit" : "Not audited yet"}
              </Label>
            </Header>

            <Form>
              <Form.Group widths="equal">
                {SCORES.map(({ key, label, colour }) => (
                  <Form.Input
                    key={key}
                    label={label}
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0 – 100"
                    style={{ background: colour }}
                    value={draft[key]}
                    onChange={(_, { value }) => setDraft((d) => ({ ...d, [key]: value }))}
                  />
                ))}
              </Form.Group>
              <Form.TextArea
                label="Notes (optional)"
                rows={2}
                value={draft.notes}
                onChange={(_, { value }) => setDraft((d) => ({ ...d, notes: String(value ?? "") }))}
              />
              <Button
                primary
                icon="save"
                content={existing ? "Update audit" : "Save audit"}
                loading={saving}
                disabled={saving || outOfRange}
                onClick={handleSave}
                type="button"
              />
              {outOfRange && (
                <Message negative size="small" content="Each score must be a number from 0 to 100." />
              )}
              {savedNote && <Message positive size="small" content="Saved." />}
              {saveError && <Message negative size="small" content={saveError} />}
            </Form>
          </>
        )}
      </Segment>

      {communityId != null && (
        <Segment>
          <Header as="h3">Recorded audits</Header>
          {audits.length === 0 ? (
            <p>Nothing recorded for this community yet.</p>
          ) : (
            <Table compact celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Block</Table.HeaderCell>
                  {SCORES.map(({ key, label }) => (
                    <Table.HeaderCell key={key}>{label}</Table.HeaderCell>
                  ))}
                  <Table.HeaderCell>Notes</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {audits.map((a) => (
                  <Table.Row key={a.blockTagId} active={a.blockTagId === blockId}>
                    <Table.Cell>{a.period}</Table.Cell>
                    <Table.Cell>{show(a.confidence)}</Table.Cell>
                    <Table.Cell>{show(a.capacity)}</Table.Cell>
                    <Table.Cell>{show(a.connection)}</Table.Cell>
                    <Table.Cell>{show(a.contribution)}</Table.Cell>
                    <Table.Cell>{a.notes ?? ""}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Segment>
      )}
    </Container>
  );
};
